import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { distinctUntilChanged, finalize } from 'rxjs';
import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  appendCatalogItemIdToReturnUrl,
  resolveDashboardReturnUrl,
} from '../utils/catalog-item-ids.util';
import {
  ItemResponse,
  ItemService,
} from '@nebalkon-workspace/storage-api';
import { ClientItemFormComponent } from './client-item-form.component';
import { CabinetServiceStatusHintComponent } from '../help/cabinet-service-status-hint.component';

@Component({
  selector: 'lib-client-item-form-page',
  imports: [TranslatePipe, ClientItemFormComponent, CabinetServiceStatusHintComponent],
  template: `
    @if (loading()) {
      <p class="client-item-form-page__loading">
        {{ 'CLIENT.CABINET.ITEM_CATALOG.LOADING' | translate }}
      </p>
    } @else if (loadError()) {
      <div class="client-item-form-page__error-block">
        <p class="client-item-form-page__error" role="alert">
          {{ loadError() | translate }}
        </p>
        @if (itemId()) {
          <button
            type="button"
            class="client-item-form-page__retry"
            (click)="retryLoad()"
          >
            {{ 'CLIENT.CABINET.ITEM_CATALOG.RETRY' | translate }}
          </button>
        }
        <lib-cabinet-service-status-hint />
      </div>
    } @else {
      <lib-client-item-form [item]="item()" (saved)="onSaved($event)" />
    }
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
    }

    .client-item-form-page__loading,
    .client-item-form-page__error {
      margin: 0;
    }

    .client-item-form-page__error-block {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    .client-item-form-page__retry {
      padding: 8px 16px;
      border: 1px solid #37beb0;
      border-radius: 8px;
      background: #fff;
      color: #37beb0;
      cursor: pointer;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientItemFormPageComponent {
  readonly #itemService = inject(ItemService);
  readonly #paths = inject(CABINET_PATHS);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #destroyRef = inject(DestroyRef);

  readonly itemId = input<string | undefined>();

  readonly loading = signal(false);
  readonly loadError = signal('');
  readonly item = signal<ItemResponse | null>(null);

  constructor() {
    toObservable(this.itemId)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe((id) => {
        if (id) {
          this.loadItem(id);
          return;
        }

        this.item.set(null);
        this.loadError.set('');
        this.loading.set(false);
      });
  }

  retryLoad(): void {
    const id = this.itemId();
    if (id) {
      this.loadItem(id);
    }
  }

  onSaved(item: ItemResponse): void {
    const returnUrl = resolveDashboardReturnUrl(
      this.#route.snapshot.queryParamMap.get('returnUrl'),
    );
    if (returnUrl) {
      void this.#router.navigateByUrl(
        appendCatalogItemIdToReturnUrl(returnUrl, item.id),
      );
      return;
    }

    void this.#router.navigateByUrl(`${this.#paths.client}/my-storage`);
  }

  private loadItem(id: string): void {
    this.loadError.set('');
    this.loading.set(true);
    this.item.set(null);

    this.#itemService
      .getAtClientItems()
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe({
        next: ({ data }) => {
          const found = (data ?? []).find((entry) => entry.id === id);
          if (!found) {
            this.loadError.set('CLIENT.CABINET.ITEM_CATALOG.NOT_FOUND');
            return;
          }
          this.item.set(found);
        },
        error: () =>
          this.loadError.set('CLIENT.CABINET.ITEM_CATALOG.LOAD_ERROR'),
      });
  }
}
